import type { ToastEntry } from "@main/types/toast";
import { type Accessor, createSignal } from "solid-js";

/**
 * §3.11 共通 UI 部品「一時的な通知表示 (5 秒で自動消去)」用の signal モジュール。
 * AppError 通知 (M1 setAppErrorNotifier) からも、 通知サーフェス (M7) からも push される。
 * `ToastEntry` の shape は types/toast.ts に置く (passive な UI 部品も同じ型を使うため)。
 */

const DEFAULT_DURATION_MS = 5_000;

const [toasts, setToasts] = createSignal<ToastEntry[]>([]);

export const getToasts: Accessor<ToastEntry[]> = toasts;

export function pushToast(
  entry: Omit<ToastEntry, "id" | "timerHandle">,
  durationMs = DEFAULT_DURATION_MS,
): string {
  const id = `t_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
  const timerHandle = window.setTimeout(() => dismissToast(id), durationMs);
  const next: ToastEntry = { id, ...entry, timerHandle };
  setToasts([...toasts(), next]);
  return id;
}

export function dismissToast(id: string): void {
  const target = toasts().find((t) => t.id === id);
  if (target?.timerHandle !== undefined) window.clearTimeout(target.timerHandle);
  setToasts(toasts().filter((t) => t.id !== id));
}

/**
 * §UC-12: 指定 notificationId に紐づくトーストを dismiss する。 PiP 側のフィードバック
 * ボタン押下からメイン画面のトーストを消すために、 session-store の markNotification* から
 * 呼ばれる。 該当が無ければ no-op。
 */
export function dismissToastByNotificationId(notificationId: string): void {
  const target = toasts().find((t) => t.notificationId === notificationId);
  if (target) dismissToast(target.id);
}
